<template>
  <PageLoadingState v-if="isLoading" />
  <PageMaintenanceState v-else-if="isMaintenancing" />
  <PageErrorState v-else-if="respError" :error="respError" />
  <MainLayout v-else-if="!isPipOnly" />
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from 'vue';

import { tre } from '@/plugins/bugsnag';
import { updateBrowserStorageFingerprintProId } from '@/store-composable/fingerprintProId';

import MainLayout from './components/MainLayout/MainLayout.vue';
import PageErrorState from './components/PageErrorState/PageErrorState.vue';
import PageLoadingState from './components/PageLoadingState/PageLoadingState.vue';
import PageMaintenanceState from './components/PageMaintenanceState/PageMaintenanceState.vue';
import { usePostMessageReceiver } from './composables/usePostMessageReceiver';
import {
  getGeneralTemplateData,
  getMaintenanceStatus,
  getTemplateData,
} from './js/api';
import { IS_DEBUG_MODE, IS_GENERAL_CMS, SDK_TOKEN, TEMPLATE_ID, TRACKER_BASE_URI } from './js/constants';
import { globalConfig } from './store/globalConfig';
import { useTemplate } from './store/templateData';

const isLoading = ref(true);
const isMaintenancing = ref(false);
const respError = ref();
const { setTemplateData, isModuleIncludingStreamRoom } = useTemplate();
const isPipOnly = computed(() => globalConfig.value.isPipOnly);

const requestTemplateData = async () => {
  try {
    if (IS_GENERAL_CMS) {
      const result = await getGeneralTemplateData(SDK_TOKEN);
      if (result.ok) {
        setTemplateData(result.data);
      }
    } else {
      const result = await getTemplateData(SDK_TOKEN, TEMPLATE_ID);
      if (result.ok) {
        setTemplateData(result.data);
      }
    }
  } catch (error) {
    tre(error);
    respError.value = error;
  }
};

const registerReloadEvent = () => {
  const { onLiveStarted, onLiveEnded, onForcedReload } = usePostMessageReceiver();
  onLiveStarted(() => requestTemplateData());
  onLiveEnded(() => requestTemplateData());
  onForcedReload(() => requestTemplateData());
};

// #region handle FingerprintProId and Tracker

// NOTE:
// - 嘗試取得 Tracker localStorage 中的 FingerprintProId 來載入 Tracker
//   並透過 stream url 傳給 Buyer
// - 若取不到，則用 Buyer 傳過來的 fingerprintProId 來載入 Tracker
// - 若 Buyer 也沒有傳過來，Tracker 則會在載入後自己呼叫 API 取得 FingerprintProId
// (CMS 與其中的 Tracker 同 domain，因此 CMS 可以存取 Tracker 的 localStorage 資料)
// REF: https://17media.atlassian.net/l/cp/2Xcr1sc2
const TRACKER_FP_PRO_ID_STORAGE_PATH = 'hut-fp-pro-id';

/** @param fpId */
const injectHandsupTracker = (fpId: string | undefined) => {
  const oldTrackerScriptEls = document.querySelectorAll('script[src*="tracker.handsup"]');
  if (oldTrackerScriptEls.length) {
    Array.from(oldTrackerScriptEls).forEach((el) => el?.remove());
  }

  const trackerScriptEl = document.createElement('script');

  const url = new URL('/tracker.min.js', TRACKER_BASE_URI);
  url.searchParams.set('sdk_token', SDK_TOKEN);

  if (fpId) {
    url.searchParams.set('fp_id', fpId);
  }

  if (IS_DEBUG_MODE) {
    url.searchParams.set('debug', '1');
  }
  trackerScriptEl.src = url.toString();
  document.head.appendChild(trackerScriptEl);
};

const registerChildFrameFingerprintProIdEvent = () => {
  const { onChildFrameFingerprintProId } = usePostMessageReceiver();
  onChildFrameFingerprintProId((fpId: string) => {
    injectHandsupTracker(fpId);
  });
};

const handleFingerprintProIdAndTracker = () => {
  const storedValue = localStorage.getItem(TRACKER_FP_PRO_ID_STORAGE_PATH);
  const fpIdFromTrackerLocalStorage = storedValue ? JSON.parse(storedValue) : null;

  if (isModuleIncludingStreamRoom.value) {
    if (!fpIdFromTrackerLocalStorage?.value) {
      // NOTE: first visit
      registerChildFrameFingerprintProIdEvent();
    } else {
      // NOTE: second or more visits
      injectHandsupTracker(undefined);
      // NOTE: update FingerprintProId to send to Buyer
      updateBrowserStorageFingerprintProId(fpIdFromTrackerLocalStorage?.value ?? null);
    }
  } else {
    injectHandsupTracker(undefined);
  }
};
// #endregion handle FingerprintProId and Tracker

onMounted(async () => {
  const result = await getMaintenanceStatus();
  if (result.ok && result.data.maintaining) {
    isMaintenancing.value = true;
    isLoading.value = false;
    return; // early return if we're maintaining
  }

  await requestTemplateData();
  isLoading.value = false;
  registerReloadEvent();
  handleFingerprintProIdAndTracker();
});
</script>
